export type AssetQueryRuleNode = {
  field?: string;
  operator?: string;
  value?: unknown;
  type?: unknown;
  condition?: string;
  rules?: AssetQueryRuleNode[];
};

const externalAttributesPrefix = "assetidentity.externalattributes.";

export type ExternalDateValueParser = (value: string) => Date | null | undefined;

export const normalizeExternalDateValueForApi = (
  value: unknown,
  parseLocalizedDate?: ExternalDateValueParser
): unknown => {
  const normalize = (item: unknown): unknown => {
    const date = item instanceof Date
      ? item
      : typeof item === "string"
        ? parseLocalizedDate?.(item)
        : undefined;
    if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
      return item;
    }
    const year = date.getFullYear().toString().padStart(4, "0");
    const month = (date.getMonth() + 1).toString().padStart(2, "0");
    const day = date.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  return Array.isArray(value) ? value.map(normalize) : normalize(value);
};

export const restoreExternalDateValueForQueryBuilder = (value: unknown): unknown => {
  const restore = (item: unknown): unknown => {
    if (typeof item !== "string") return item;
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(item);
    if (!match) return item;
    const [, year, month, day] = match.map(Number);
    const restored = new Date(year, month - 1, day);
    return restored.getFullYear() === year && restored.getMonth() === month - 1 && restored.getDate() === day
      ? restored
      : item;
  };

  return Array.isArray(value) ? value.map(restore) : restore(value);
};

export type HierarchyConsolidationGroup = {
  assetClassId: string;
  name: string;
  setName: string;
  dataType: number;
  individualIds: string[];
  consolidatedField: string;
};

export type HierarchyConsolidationResult = {
  individualToConsolidated: Map<string, string>;
  consolidatedGroups: Map<string, HierarchyConsolidationGroup>;
};

export const buildHierarchyConsolidationMap = (assetClasses: IAssetClassDetail[]): HierarchyConsolidationResult => {
  const individualToConsolidated = new Map<string, string>();
  const consolidatedGroups = new Map<string, HierarchyConsolidationGroup>();

  assetClasses.forEach((assetClass) => {
    if (!assetClass?.ID) return;
    const hierarchyAttributes = assetClass.HierarchyAttributeDefinitions ?? [];
    if (hierarchyAttributes.length === 0) return;

    const groupKey = (attribute: IAttributeDefinitionExt) =>
      `${attribute.Name ?? ""}\0${attribute.SetName ?? ""}\0${attribute.DataType ?? 1}`;

    const groups = new Map<string, IAttributeDefinitionExt[]>();
    hierarchyAttributes.forEach((attribute) => {
      if (!attribute?.ID) return;
      const key = groupKey(attribute);
      let group = groups.get(key);
      if (!group) {
        group = [];
        groups.set(key, group);
      }
      group.push(attribute);
    });

    groups.forEach((attributes) => {
      const ids = attributes.map((attribute) => attribute.ID).sort();
      const consolidatedField = ids.join("|");
      const representative = attributes[0];

      const groupMeta: HierarchyConsolidationGroup = {
        assetClassId: assetClass.ID,
        name: representative.Name,
        setName: representative.SetName,
        dataType: representative.DataType,
        individualIds: ids,
        consolidatedField,
      };

      consolidatedGroups.set(`${assetClass.ID}/${consolidatedField}`, groupMeta);
      ids.forEach((id) => {
        individualToConsolidated.set(`${assetClass.ID}/${id}`, consolidatedField);
      });
    });
  });

  return { individualToConsolidated, consolidatedGroups };
};

export const resolveConsolidatedRules = (
  rules: AssetQueryRuleNode | undefined,
  assetClasses: IAssetClassDetail[]
): AssetQueryRuleNode | undefined => {
  if (!rules) return undefined;

  const consolidation = buildHierarchyConsolidationMap(assetClasses);
  const attributeToAssetClassId = new Map<string, string>();

  assetClasses.forEach((assetClass) => {
    (assetClass.HierarchyAttributeDefinitions ?? []).forEach((attribute) => {
      if (attribute?.ID && assetClass?.ID) {
        attributeToAssetClassId.set(attribute.ID, assetClass.ID);
      }
    });
  });

  const remapAttributePart = (attributePart: string, assetClassId: string): string => {
    const ids = attributePart.split("|");
    for (const id of ids) {
      const current = consolidation.individualToConsolidated.get(`${assetClassId}/${id}`);
      if (current) {
        return current;
      }
    }
    return attributePart;
  };

  const remapRule = (rule: AssetQueryRuleNode): AssetQueryRuleNode => {
    if (!rule) return rule;

    if (Array.isArray(rule.rules)) {
      return { ...rule, rules: rule.rules.map(remapRule) };
    }

    if (typeof rule.field !== "string") return rule;

    const slashParts = rule.field.split("/");
    if (slashParts.length === 2) {
      const assetClassId = slashParts[0];
      const attributePart = slashParts[1];
      const remapped = remapAttributePart(attributePart, assetClassId);
      if (remapped !== attributePart) {
        return { ...rule, field: `${assetClassId}/${remapped}` };
      }
      return rule;
    }

    const hierarchyPrefix = "assetidentity.hierarchyattributes.";
    if (rule.field.startsWith(hierarchyPrefix)) {
      const attributePart = rule.field.substring(hierarchyPrefix.length);
      const ids = attributePart.split("|");
      let assetClassId: string | undefined;
      for (const id of ids) {
        assetClassId = attributeToAssetClassId.get(id);
        if (assetClassId) break;
      }

      if (assetClassId) {
        const remapped = remapAttributePart(attributePart, assetClassId);
        if (remapped !== attributePart) {
          return { ...rule, field: hierarchyPrefix + remapped };
        }
      }
    }

    return rule;
  };

  return remapRule(rules);
};

// Remove redundant single-child wrapper groups so restored queries are in the
// simplest shape for the QueryBuilder control.
export const collapseRedundantRuleGroups = (
  rules: AssetQueryRuleNode | null | undefined
): AssetQueryRuleNode | undefined => {
  if (!rules) {
    return undefined;
  }

  const collapseRule = (rule: AssetQueryRuleNode): AssetQueryRuleNode => {
    if (!Array.isArray(rule.rules)) {
      return rule;
    }

    const collapsedChildren = rule.rules.map(collapseRule);
    const collapsedGroup = {
      ...rule,
      rules: collapsedChildren,
    };

    if (collapsedChildren.length === 1 && Array.isArray(collapsedChildren[0]?.rules)) {
      return collapsedChildren[0];
    }

    return collapsedGroup;
  };

  return collapseRule(rules);
};

export const convertApiRulesToQueryBuilderRules = (
  apiRules: AssetQueryRuleNode | null | undefined,
  assetClasses: IAssetClassDetail[]
): AssetQueryRuleNode | undefined => {
  if (!apiRules) {
    return undefined;
  }

  const attributeToAssetClassMap = new Map<string, string>();
  const externalAttributeToAssetClassMap = new Map<string, string>();
  const externalDateAttributeIds = new Set<string>();
  const assetClassIdToNameMap = new Map<string, string>();

  assetClasses.forEach((assetClass) => {
    if (assetClass?.ID && assetClass?.Name && !assetClassIdToNameMap.has(assetClass.ID)) {
      assetClassIdToNameMap.set(assetClass.ID, assetClass.Name);
    }

    (assetClass.AttributeDefinitions ?? []).forEach((attribute) => {
      if (attribute?.ID && assetClass?.ID && !attributeToAssetClassMap.has(attribute.ID)) {
        attributeToAssetClassMap.set(attribute.ID, assetClass.ID);
      }
    });

    (assetClass.HierarchyAttributeDefinitions ?? []).forEach((attribute) => {
      if (attribute?.ID && assetClass?.ID && !attributeToAssetClassMap.has(attribute.ID)) {
        attributeToAssetClassMap.set(attribute.ID, assetClass.ID);
      }
    });

    ((assetClass as IAssetClassDetail & { ExternalAttributeDefinitions?: IExternalAttributeDefinition[] }).ExternalAttributeDefinitions ?? [])
      .forEach((attribute) => {
        if (attribute?.ID && assetClass?.ID && !externalAttributeToAssetClassMap.has(attribute.ID)) {
          externalAttributeToAssetClassMap.set(attribute.ID, assetClass.ID);
        }
        if (attribute?.ID && attribute.DataType === AttributeDataType.Date) {
          externalDateAttributeIds.add(attribute.ID);
        }
      });
  });

  const consolidation = buildHierarchyConsolidationMap(assetClasses);

  const mapAssetClassValueToName = (value: unknown): unknown => {
    if (Array.isArray(value)) {
      return value.map((item) => {
        if (typeof item !== "string") {
          return item;
        }
        return assetClassIdToNameMap.get(item) ?? item;
      });
    }

    if (typeof value === "string") {
      return assetClassIdToNameMap.get(value) ?? value;
    }

    return value;
  };

  const convertRule = (rule: AssetQueryRuleNode): AssetQueryRuleNode => {
    if (!rule) {
      return rule;
    }

    if (Array.isArray(rule.rules)) {
      return {
        ...rule,
        rules: rule.rules.map(convertRule),
      };
    }

    if (typeof rule.field !== "string") {
      return rule;
    }

    const attributesPrefix = "assetidentity.attributes.";
    const hierarchyPrefix = "assetidentity.hierarchyattributes.";
    if (rule.field === "assetidentity.assetclass.id") {
      return {
        ...rule,
        field: "assetidentity.assetclass.name",
        value: mapAssetClassValueToName(rule.value),
      };
    }

    if (rule.field === "assetidentity.assetclass.name") {
      return {
        ...rule,
        value: mapAssetClassValueToName(rule.value),
      };
    }

    if (rule.field.startsWith(externalAttributesPrefix)) {
      const externalAttributeId = rule.field.substring(externalAttributesPrefix.length);
      const assetClassId = externalAttributeToAssetClassMap.get(externalAttributeId);
      if (assetClassId) {
        return {
          ...rule,
          field: `${assetClassId}/${externalAttributeId}`,
          value: externalDateAttributeIds.has(externalAttributeId)
            ? restoreExternalDateValueForQueryBuilder(rule.value)
            : rule.value,
        };
      }
    } else if (rule.field.startsWith(attributesPrefix)) {
      const attributeId = rule.field.substring(rule.field.lastIndexOf(".") + 1);
      const assetClassId = attributeToAssetClassMap.get(attributeId);
      if (assetClassId) {
        return {
          ...rule,
          field: `${assetClassId}/${attributeId}`,
        };
      }
    } else if (rule.field.startsWith(hierarchyPrefix)) {
      const attributePart = rule.field.substring(hierarchyPrefix.length);
      const individualIds = attributePart.split("|");
      let assetClassId: string | undefined;

      for (const id of individualIds) {
        assetClassId = attributeToAssetClassMap.get(id);
        if (assetClassId) break;
      }

      if (assetClassId) {
        let consolidatedField = attributePart;
        for (const id of individualIds) {
          const current = consolidation.individualToConsolidated.get(`${assetClassId}/${id}`);
          if (current) {
            consolidatedField = current;
            break;
          }
        }

        return {
          ...rule,
          field: `${assetClassId}/${consolidatedField}`,
        };
      }
    }

    return rule;
  };

  return convertRule(apiRules);
};
